import React from 'react';
import { Section } from '@/pages/LandingComplete';
import SectionHeader from './SectionHeader';
import { Button } from '@/components/ui/button';
import { CheckCircle, Shield, Laptop2, Users, Zap } from 'lucide-react';

interface PricePackage {
  name: string;
  description: string;
  price: string;
  priceUnit?: string;
  features: string[];
  recommended?: boolean;
  ctaText?: string;
}

interface PriceCategory {
  icon?: string;
  title: string;
  subtitle?: string;
  intro?: string;
  packages: PricePackage[];
}

interface PricesContent {
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  categories?: PriceCategory[];
}

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Laptop2,
  Users,
  Shield,
  Zap,
};

export function DynamicPrices({ content }: { content: PricesContent }) {
  const categories = content.categories || [];

  return (
    <Section id="preise">
      <SectionHeader 
        number="04" 
        title={content.title || "Faire"} 
        highlight={content.titleHighlight || "Preise"}
        subtitle={content.subtitle || "Einfach, verständlich und ohne Fachchinesisch."}
      />

      <div className="grid gap-12 lg:grid-cols-2">
        {categories.map((category, catIdx) => {
          const IconComponent = category.icon && iconMap[category.icon] ? iconMap[category.icon] : Laptop2;
          const iconColors = ['from-blue-500 to-blue-600', 'from-green-500 to-green-600'];
          
          return (
            <div key={category.title + catIdx} className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200/60 hover:shadow-2xl transition-all duration-500">
              <div className="flex items-center gap-3 mb-6">
                <div className={`p-3 rounded-2xl bg-gradient-to-br ${iconColors[catIdx % iconColors.length]} shadow-lg`}>
                  <IconComponent size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">{category.title}</h3>
                  {category.subtitle && <p className="text-slate-600">{category.subtitle}</p>}
                </div>
              </div>

              {category.intro && (
                <p className="text-slate-600 mb-6 text-sm leading-relaxed bg-blue-50 p-4 rounded-xl border border-blue-100">
                  {category.intro}
                </p>
              )}

              <div className="space-y-4">
                {category.packages.map((pkg, pkgIdx) => (
                  <div 
                    key={pkg.name + pkgIdx} 
                    className={`group relative rounded-2xl border-2 p-6 transition-all hover:shadow-lg ${
                      pkg.recommended 
                        ? 'border-[--brand-primary] bg-blue-50/30 shadow-lg' 
                        : 'border-slate-200 hover:border-[--brand-primary]'
                    }`}
                  >
                    {pkg.recommended && (
                      <div className="absolute -top-3 left-6">
                        <span className="bg-gradient-to-r from-[--brand-primary] to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          ⭐ Empfohlen
                        </span>
                      </div>
                    )}
                    
                    <div className={`flex flex-col md:flex-row md:items-start md:justify-between gap-4 ${pkg.recommended ? 'mt-2' : ''}`}>
                      <div className="flex-1">
                        <div className="font-bold text-slate-800 text-lg mb-1">{pkg.name}</div>
                        <p className="text-sm text-slate-600 mb-3 italic">{pkg.description}</p>
                        <ul className="space-y-1">
                          {pkg.features.map((feature, fIdx) => (
                            <li key={fIdx} className="flex items-start gap-2 text-slate-600 text-sm">
                              <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="md:ml-6 flex flex-col items-end gap-2">
                        <div className="bg-gradient-to-br from-[--brand-primary] to-blue-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                          {pkg.price}{pkg.priceUnit ? ` / ${pkg.priceUnit}` : ''}
                        </div>
                        <Button
                          onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                          variant={pkg.recommended ? "default" : "outline"}
                          size="sm"
                          className={pkg.recommended 
                            ? "text-sm bg-[--brand-primary] hover:bg-[--brand-primary-dark] text-white"
                            : "text-sm border-[--brand-primary] text-[--brand-primary] hover:bg-[--brand-primary] hover:text-white"
                          }
                        >
                          {pkg.ctaText || 'Jetzt anfragen'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
